import { useId, useState } from 'react';
import { contact, site } from '../content';

type Errors = Partial<Record<'name' | 'email' | 'message', string>>;
type State = 'idle' | 'sending' | 'sent' | 'error';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function ContactForm() {
  const uid = useId();
  const [state, setState] = useState<State>('idle');
  const [errors, setErrors] = useState<Errors>({});
  const [message, setMessage] = useState('');

  const fid = (n: string) => `${uid}-${n}`;

  const validate = (data: FormData): Errors => {
    const e: Errors = {};
    const name = String(data.get('name') ?? '').trim();
    const email = String(data.get('email') ?? '').trim();
    const msg = String(data.get('message') ?? '').trim();
    if (name.length < 2) e.name = 'Tell me who you are.';
    if (!EMAIL_RE.test(email)) e.email = 'Enter an email I can reply to.';
    if (msg.length < 20) e.message = 'A little more detail, please. Twenty characters at least.';
    return e;
  };

  const onSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    if (state === 'sending') return;
    const form = ev.currentTarget;
    const data = new FormData(form);

    const found = validate(data);
    setErrors(found);
    if (Object.keys(found).length) {
      setState('idle');
      setMessage('');
      const first = form.querySelector<HTMLElement>('[data-invalid="true"] input, [data-invalid="true"] textarea');
      first?.focus();
      return;
    }

    setState('sending');
    setMessage('');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(Object.fromEntries(data)),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(body.error || 'Something went wrong on my end.');
      setState('sent');
      setMessage(contact.success);
      form.reset();
    } catch (err) {
      setState('error');
      setMessage(
        `${err instanceof Error ? err.message : 'Something went wrong on my end.'} Email me directly at ${site.email}.`,
      );
    }
  };

  const clearError = (k: keyof Errors) =>
    setErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));

  const f = contact.fields;

  return (
    <form className="contact-panel" onSubmit={onSubmit} noValidate>
      <div className="form-rows">
        <div className="row-2">
          <div className="field-row" data-invalid={errors.name ? 'true' : undefined}>
            <label htmlFor={fid('name')}>{f.name.label}</label>
            <input
              id={fid('name')}
              name="name"
              type="text"
              autoComplete="name"
              placeholder={f.name.placeholder}
              aria-describedby={errors.name ? fid('name-err') : undefined}
              aria-invalid={errors.name ? true : undefined}
              onInput={() => clearError('name')}
            />
            {errors.name && (
              <span className="field-error" id={fid('name-err')}>
                {errors.name}
              </span>
            )}
          </div>

          <div className="field-row" data-invalid={errors.email ? 'true' : undefined}>
            <label htmlFor={fid('email')}>{f.email.label}</label>
            <input
              id={fid('email')}
              name="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder={f.email.placeholder}
              aria-describedby={errors.email ? fid('email-err') : undefined}
              aria-invalid={errors.email ? true : undefined}
              onInput={() => clearError('email')}
            />
            {errors.email && (
              <span className="field-error" id={fid('email-err')}>
                {errors.email}
              </span>
            )}
          </div>
        </div>

        <div className="field-row">
          <label htmlFor={fid('company')}>
            {f.company.label} <span className="opt">optional</span>
          </label>
          <input
            id={fid('company')}
            name="company"
            type="text"
            autoComplete="organization"
            placeholder={f.company.placeholder}
          />
        </div>

        <div className="field-row" data-invalid={errors.message ? 'true' : undefined}>
          <label htmlFor={fid('message')}>{f.message.label}</label>
          <textarea
            id={fid('message')}
            name="message"
            rows={6}
            placeholder={f.message.placeholder}
            aria-describedby={errors.message ? fid('message-err') : undefined}
            aria-invalid={errors.message ? true : undefined}
            onInput={() => clearError('message')}
          />
          {errors.message && (
            <span className="field-error" id={fid('message-err')}>
              {errors.message}
            </span>
          )}
        </div>
      </div>

      {/* Bot bait. Real people never see it; anything that fills it is dropped. */}
      <div className="hp" aria-hidden="true">
        <label htmlFor={fid('website')}>Website</label>
        <input id={fid('website')} name="website" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      <div className="form-foot">
        <button className="btn" type="submit" disabled={state === 'sending'}>
          {state === 'sending' ? contact.sending : contact.submit}
          <span aria-hidden="true">→</span>
        </button>
        <p
          className="form-status"
          data-tone={state === 'sent' ? 'ok' : state === 'error' ? 'err' : undefined}
          role="status"
          aria-live="polite"
        >
          {message}
        </p>
      </div>
    </form>
  );
}
